/**
 * Format a number as currency
 */
export const formatCurrency = (
  value: number,
  currency = 'USD',
  compact = false
): string => {
  if (compact && Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  if (compact && Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format a percentage value
 */
export const formatPercent = (value: number, decimals = 2): string => {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
};

/**
 * Format a date string
 */
export const formatDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a datetime string
 */
export const formatDateTime = (dateStr: string): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateStr: string): string => {
  if (!dateStr) return '—';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
};

/**
 * Get Tailwind color classes for PAMM statuses
 */
export const getPAMMStatusColor = (status: string): string => {
  const map: Record<string, string> = {
    active: 'bg-green-100 text-green-800 border-green-200',
    inactive: 'bg-gray-100 text-gray-600 border-gray-200',
    suspended: 'bg-red-100 text-red-700 border-red-200',
    closed: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    pending: 'bg-amber-100 text-amber-700 border-amber-200',

    completed: 'bg-green-100 text-green-800 border-green-200',
    approved: 'bg-green-100 text-green-800 border-green-200',
    rejected: 'bg-red-100 text-red-700 border-red-200',
    processing: 'bg-blue-100 text-blue-700 border-blue-200',

    open: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-200',

    distributed: 'bg-green-100 text-green-800 border-green-200',

    ready: 'bg-green-100 text-green-800 border-green-200',
    generating: 'bg-blue-100 text-blue-700 border-blue-200',
    failed: 'bg-red-100 text-red-700 border-red-200',

    success: 'bg-green-100 text-green-800 border-green-200',

    info: 'bg-blue-100 text-blue-700 border-blue-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    error: 'bg-red-100 text-red-700 border-red-200',

    low: 'bg-green-100 text-green-800 border-green-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    high: 'bg-red-100 text-red-700 border-red-200',

    buy: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    sell: 'bg-rose-100 text-rose-700 border-rose-200',

    verified: 'bg-green-100 text-green-800 border-green-200',
  };
  return (
    map[status?.toLowerCase()] ?? 'bg-gray-100 text-gray-600 border-gray-200'
  );
};

/**
 * Get a Tailwind text color for profit/loss
 */
export const getProfitColor = (value: number): string => {
  if (value > 0) return 'text-green-600';
  if (value < 0) return 'text-red-600';
  return 'text-gray-500';
};

/**
 * Capitalize first letter
 */
export const capitalize = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
};

/**
 * Format a large number compactly
 */
export const formatCompact = (value: number): string => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toString();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const exportToCSV = (
  data: Record<string, any>[],
  filename: string
): void => {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row =>
    headers.map(h => JSON.stringify(row[h] ?? '')).join(',')
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Get notification icon color class
 */
export const getNotificationTypeColor = (type: string): string => {
  const map: Record<string, string> = {
    info: 'text-blue-500',
    success: 'text-green-500',
    warning: 'text-amber-500',
    error: 'text-red-500',
  };
  return map[type] ?? 'text-gray-500';
};

/**
 * Get activity icon type from activity type
 */
export const getActivityIcon = (type: string): string => {
  const map: Record<string, string> = {
    deposit: 'arrow-down-circle',
    withdrawal: 'arrow-up-circle',
    distribution: 'divide-circle',
    trade: 'trending-up',
    fund: 'briefcase',
  };
  return map[type] ?? 'activity';
};
